// Admin Bookings Page — Firestore-backed
import { renderHeader, renderBottomNav } from '../components/header.js';
import { renderBookingCard, bindBookingActions } from '../components/booking-card.js';
import { toastSuccess } from '../components/toast.js';
import { requireAdmin } from '../auth.js';
import { getAllBookings, updateBookingDB, getAllUsers } from '../services/db.js';
import { demoBookings, updateDemoBooking } from '../data/demo-store.js';
import { isFirebaseReady } from '../firebase.js';
import { notifyBookingConfirmed, notifyBookingCompleted } from '../services/api.js';
import { showConfirm, showPrompt, showAlert } from '../utils/dialog.js';

let activeFilter = 'all';
let searchQuery = '';
let currentPage = 1;
const itemsPerPage = 10;

export async function renderAdminBookings() {
  const allowed = await requireAdmin();
  if (!allowed) return;

  const app = document.getElementById('app');

  // Show loading
  app.innerHTML = `
    ${renderHeader()}
    <div class="page">
      <h2 class="page-title">📋 จัดการการจอง</h2>
      <div style="text-align:center; padding: var(--space-xl);">
        <div class="loading-spinner"></div>
        <p class="text-muted mt-md">กำลังโหลด...</p>
      </div>
    </div>
    ${renderBottomNav('admin-bookings')}
  `;

  // Load bookings, users, and settings
  const allBookingsRaw = isFirebaseReady() ? await getAllBookings() : demoBookings;
  const allUsers = isFirebaseReady() ? await getAllUsers() : [];
  const settings = isFirebaseReady() ? await import('../services/db.js').then(m => m.getAdminSettingsDB()) : {};

  // Enrich bookings with LINE profile data
  const allBookings = allBookingsRaw.map(b => {
    const user = allUsers.find(u => u.id === b.user_id);
    if (user) {
      return { ...b, line_name: user.name, line_picture: user.pictureUrl, phone: user.phone || '' };
    }
    return b;
  });

  // Filter sequentially
  let filteredBookings = activeFilter === 'all'
    ? allBookings
    : allBookings.filter(b => b.status === activeFilter);
    
  if (searchQuery.trim() !== '') {
    const q = searchQuery.toLowerCase().trim();
    filteredBookings = filteredBookings.filter(b => 
      (b.customer_name && b.customer_name.toLowerCase().includes(q)) ||
      (b.line_name && b.line_name.toLowerCase().includes(q)) ||
      (b.phone && b.phone.includes(q)) ||
      (b.customer_details?.question && b.customer_details.question.toLowerCase().includes(q))
    );
  }

  // Calculate stats based on search or overall
  const filters = [
    { id: 'all', label: 'ทั้งหมด', count: allBookings.length },
    { id: 'pending', label: 'รอยืนยัน', count: allBookings.filter(b => b.status === 'pending').length },
    { id: 'confirmed', label: 'ยืนยันแล้ว', count: allBookings.filter(b => b.status === 'confirmed').length },
    { id: 'cancelled', label: 'ยกเลิก', count: allBookings.filter(b => b.status === 'cancelled').length }
  ];

  // Pagination logic
  const totalItems = filteredBookings.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  if (currentPage > totalPages) currentPage = totalPages;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedBookings = filteredBookings.slice(startIndex, startIndex + itemsPerPage);

  app.innerHTML = `
    ${renderHeader()}
    <div class="page">
      <h2 class="page-title">📋 จัดการการจอง</h2>

      <div class="form-group mb-sm">
        <input type="text" id="admin-search-input" class="form-input" placeholder="🔍 ค้นหาชื่อ, เบอร์โทร, ไลน์..." value="${searchQuery}" />
      </div>

      <div style="display:flex; gap: var(--space-xs); overflow-x:auto; padding-bottom: var(--space-sm); margin-bottom: var(--space-md); white-space: nowrap;">
        ${filters.map(f => `
          <button class="btn btn-sm ${activeFilter === f.id ? 'btn-primary' : 'btn-outline'}" 
                  data-filter="${f.id}" style="flex-shrink:0;">
            ${f.label} (${f.count})
          </button>
        `).join('')}
      </div>

      <div id="admin-booking-list">
        ${paginatedBookings.length > 0
      ? paginatedBookings.map(b => renderBookingCard(b, { showActions: true, line_oa_bot_id: settings?.line_oa_bot_id || 'U653089ad031d2896c6431fcb359e0c64' })).join('')
      : `
          <div class="empty-state">
            <div class="empty-state-icon">📭</div>
            <div class="empty-state-title">ไม่มีรายการ</div>
            <div class="empty-state-text">ไม่พบรายการจองในหมวดนี้</div>
          </div>
        `}
      </div>

      ${totalPages > 1 ? `
      <div style="display:flex; justify-content:space-between; align-items:center; margin-top: var(--space-lg); padding-bottom:var(--space-xxl);">
        <button class="btn btn-sm btn-outline ${currentPage === 1 ? 'disabled' : ''}" id="btn-prev-page">← ก่อนหน้า</button>
        <span class="text-sm text-muted">หน้าที่ ${currentPage} / ${totalPages}</span>
        <button class="btn btn-sm btn-outline ${currentPage === totalPages ? 'disabled' : ''}" id="btn-next-page">หน้าถัดไป →</button>
      </div>
      ` : '<div style="padding-bottom:var(--space-xxl);"></div>'}
    </div>
    ${renderBottomNav('admin-bookings')}
  `;

  // Bind Listeners
  document.getElementById('admin-search-input')?.addEventListener('input', (e) => {
    searchQuery = e.target.value;
    currentPage = 1; // reset to first page on search
    renderAdminBookings();
  });

  // Bind filters
  document.querySelectorAll('[data-filter]').forEach(btn => {
    btn.addEventListener('click', () => {
      activeFilter = btn.dataset.filter;
      currentPage = 1;
      renderAdminBookings();
    });
  });

  document.getElementById('btn-prev-page')?.addEventListener('click', () => {
    if (currentPage > 1) {
      currentPage--;
      renderAdminBookings();
    }
  });

  document.getElementById('btn-next-page')?.addEventListener('click', () => {
    if (currentPage < totalPages) {
      currentPage++;
      renderAdminBookings();
    }
  });

  // Bind booking actions
  const listContainer = document.getElementById('admin-booking-list');
  if (listContainer) {
    bindBookingActions(listContainer, {
      onApprove: async (id) => {
        let booking = allBookings.find(b => b.id === id);
        if (isFirebaseReady()) {
          await updateBookingDB(id, { status: 'confirmed' });
        } else {
          updateDemoBooking(id, { status: 'confirmed' });
        }
        
        // --- Send automated notification via cPanel Backend ---
        if (booking) {
          booking.status = 'confirmed'; // Update status locally for template rendering if needed
          await notifyBookingConfirmed(booking);
        }

        toastSuccess('อนุมัติการจองแล้ว ✅');
        renderAdminBookings();
      },
      onReject: async (id) => {
        if (await showConfirm('ต้องการปฏิเสธการจองนี้?')) {
          let booking = allBookings.find(b => b.id === id);
          if (isFirebaseReady()) {
            await updateBookingDB(id, { status: 'cancelled' });
          } else {
            updateDemoBooking(id, { status: 'cancelled' });
          }
          
          if (booking) {
            import('../services/api.js').then(({ sendNotification }) => {
              const msg = `❌ การจองคิวถูกยกเลิก\n\n📅 ${booking.date} เวลา ${booking.time} น.\n\nหากได้รับความไม่สะดวก ขออภัยมา ณ ที่นี้ค่ะ`;
              sendNotification(booking.user_id, msg);
            });
          }

          toastSuccess('ปฏิเสธการจองแล้ว');
          renderAdminBookings();
        }
      },
      onComplete: async (id) => {
        if (await showConfirm('ยืนยันว่าดูดวงคิวนี้เสร็จสิ้นแล้ว?')) {
          let booking = allBookings.find(b => b.id === id);
          if (isFirebaseReady()) {
            await updateBookingDB(id, { status: 'completed' });
          } else {
            updateDemoBooking(id, { status: 'completed' });
          }
          
          if (booking) {
            booking.status = 'completed';
            await notifyBookingCompleted(booking);
          }

          toastSuccess('บันทึกสถานะเสร็จสิ้นแล้ว ✅');
          renderAdminBookings();
        }
      },
      onSendMessage: async (id, userId) => {
        const msg = await showPrompt('พิมพ์ข้อความที่ต้องการส่งไปหาลูกค้าคนนี้', '💬 ส่งข้อความจากระบบ');
        if (!msg || msg.trim() === '') return;
        try {
          const btn = listContainer.querySelector(`.booking-msg-btn[data-id="${id}"]`);
          if (btn) btn.innerHTML = '⏳ กำลังส่ง...';
          
          // Use dynamic import so we don't need to add import at the top
          const { sendNotification } = await import('../services/api.js');
          await sendNotification(userId, msg.trim());
          
          await showAlert('ส่งข้อความสำเร็จแล้ว', '✅ สำเร็จ');
          if (btn) btn.innerHTML = '💬 ส่งข้อความจากระบบ';
        } catch(e) {
          await showAlert('เกิดข้อผิดพลาดในการส่งข้อความ: ' + e.message, '❌ ผิดพลาด');
          const btn = listContainer.querySelector(`.booking-msg-btn[data-id="${id}"]`);
          if (btn) btn.innerHTML = '💬 ส่งข้อความจากระบบ';
        }
      }
    });
  }
}
